import React, { useState } from 'react'
import PropTypes from 'prop-types'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  makeStyles,
  Tooltip,
} from '@material-ui/core'
import { Warning } from '@material-ui/icons'
import { useTranslate } from 'react-admin'
import useHistoryPush from '../../hooks/useHistoryPush'
import playerPerGameTableStyles from './playerPerGameTable.styles'
import formatFullName from '../../utils/helpers/practicePlayersMapper/formatFullName'
import PlayerMatchingDropdown from './PlayerMatchingDropdown'
import { useCmsScope } from '../../context/useCmsScope'
import { getVisibleColumnGroups } from '../../utils/helpers/displayStatsMapping'

const PlayersPerGameTable = ({ players, teamOnlyStats, totalStats, eventTeamId, seasonTeamId, gameId, displayStats }) => {
  const useStyles = makeStyles(playerPerGameTableStyles)
  const classes = useStyles()
  const translate = useTranslate()
  const { redirect } = useHistoryPush()
  // Matching a game player to a roster player is a write: not for a paired club reading it.
  const { readOnly } = useCmsScope()
  const [expandedRow, setExpandedRow] = useState(null)

  if (!players || players.length === 0) {
    return null
  }

  const fmtPerc = (value, total) => (total == 0 ? '-' : `${Math.round((value / total) * 100)}%`)
  const fmtRatio = (value, total) => (total == 0 ? '-' : `${value}/${total}`)

  const playerObjects = players.map((player) => {
    return {
      id: player.playerId,
      gameTeamPlayerId: player.gameTeamPlayerId,
      fullName: player.fullName,
      jerseyNumber: player.jersey,
      played: player.played,
      minutes: player.stats.minutes.slice(3),
      points: player.stats.points,
      twoPointer: fmtRatio(player.stats.made2Pointers, player.stats.attempted2Pointers),
      avgTwoPoints: fmtPerc(player.stats.made2Pointers, player.stats.attempted2Pointers),
      threePointer: fmtRatio(player.stats.made3Pointers, player.stats.attempted3Pointers),
      avgThreePoints: fmtPerc(player.stats.made3Pointers, player.stats.attempted3Pointers),
      freeThrows: fmtRatio(player.stats.made1Pointers, player.stats.attempted1Pointers),
      avgFreeThrows: fmtPerc(player.stats.made1Pointers, player.stats.attempted1Pointers),
      offensiveRebounds: player.stats.offensiveRebounds,
      defensiveRebounds: player.stats.defensiveRebounds,
      totalRebounds: player.stats.rebounds,
      assists: player.stats.assists,
      steals: player.stats.steals,
      turnOvers: player.stats.turnovers,
      blocks: player.stats.blocks,
      blocksAgainst: player.stats.blocksAgainst,
      foulsCommited: player.stats.fouls,
      foulsDrawn: player.stats.foulsDrawn,
      pir: player.stats.pir,
    }
  })

  const visibleGroups = getVisibleColumnGroups(displayStats)

  const didPlay = (player) => player.played

  const playedPlayers = playerObjects
    .filter(didPlay)
    .sort((a, b) => a.jerseyNumber - b.jerseyNumber)

  const didNotPlayPlayers = playerObjects
    .filter((player) => !didPlay(player))
    .sort((a, b) => a.jerseyNumber - b.jerseyNumber)

  const matchedPlayerIds = playerObjects
    .filter((player) => player.id)
    .map((player) => player.id)

  const handleRowClick = (playerId, player) => {
    if (playerId) {
      redirect(`/stats/player/${playerId}`)
    } else if (!readOnly) {
      setExpandedRow(expandedRow === player.gameTeamPlayerId ? null : player.gameTeamPlayerId)
    }
  }

  const headerGroupsConfig = [
    {
      displayName: 'Minutes',
      internalKeys: ['minutes'],
      headerLabels: ['MIN'],
      colSpan: 1,
      render: (player) => <TableCell key="minutes" className={classes.tableCell}>{player.minutes}</TableCell>,
      renderTotal: (totalStats) => <TableCell key="minutesTotal" className={classes.tableCell}></TableCell>,
      renderTeamOnly: (teamOnlyStats) => <TableCell key="minutesTeamOnly" className={classes.tableCell}></TableCell>,
    },
    {
      displayName: 'Points',
      internalKeys: ['points'],
      headerLabels: ['PTS'],
      colSpan: 1,
      render: (player) => <TableCell key="points" className={classes.tableCell}>{player.points}</TableCell>,
      renderTotal: (totalStats) => <TableCell key="pointsTotal" className={classes.tableCell}>{totalStats.points}</TableCell>,
      renderTeamOnly: (teamOnlyStats) => <TableCell key="pointsTeamOnly" className={classes.tableCell}></TableCell>,
    },
    {
      displayName: '2FG',
      internalKeys: ['twoPointer', 'avgTwoPoints'],
      headerLabels: ['M/A', '%'],
      colSpan: 2,
      render: (player) => (
        <React.Fragment key="twoPointer">
          <TableCell className={classes.tableCell}>{player.twoPointer}</TableCell>
          <TableCell className={classes.tableCell}>{player.avgTwoPoints}</TableCell>
        </React.Fragment>
      ),
      renderTotal: (totalStats) => (
        <React.Fragment key="twoPointerTotal">
          <TableCell className={classes.tableCell}>{fmtRatio(totalStats.made2Pointers, totalStats.attempted2Pointers)}</TableCell>
          <TableCell className={classes.tableCell}>{fmtPerc(totalStats.made2Pointers, totalStats.attempted2Pointers)}</TableCell>
        </React.Fragment>
      ),
      renderTeamOnly: (teamOnlyStats) => (
        <React.Fragment key="twoPointerTeamOnly">
          <TableCell className={classes.tableCell}></TableCell>
          <TableCell className={classes.tableCell}></TableCell>
        </React.Fragment>
      ),
    },
    {
      displayName: '3FG',
      internalKeys: ['threePointer', 'avgThreePoints'],
      headerLabels: ['M/A', '%'],
      colSpan: 2,
      render: (player) => (
        <React.Fragment key="threePointer">
          <TableCell className={classes.tableCell}>{player.threePointer}</TableCell>
          <TableCell className={classes.tableCell}>{player.avgThreePoints}</TableCell>
        </React.Fragment>
      ),
      renderTotal: (totalStats) => (
        <React.Fragment key="threePointerTotal">
          <TableCell className={classes.tableCell}>{fmtRatio(totalStats.made3Pointers, totalStats.attempted3Pointers)}</TableCell>
          <TableCell className={classes.tableCell}>{fmtPerc(totalStats.made3Pointers, totalStats.attempted3Pointers)}</TableCell>
        </React.Fragment>
      ),
      renderTeamOnly: (teamOnlyStats) => (
        <React.Fragment key="threePointerTeamOnly">
          <TableCell className={classes.tableCell}></TableCell>
          <TableCell className={classes.tableCell}></TableCell>
        </React.Fragment>
      ),
    },
    {
      displayName: 'FT',
      internalKeys: ['freeThrows', 'avgFreeThrows'],
      headerLabels: ['M/A', '%'],
      colSpan: 2,
      render: (player) => (
        <React.Fragment key="freeThrows">
          <TableCell className={classes.tableCell}>{player.freeThrows}</TableCell>
          <TableCell className={classes.tableCell}>{player.avgFreeThrows}</TableCell>
        </React.Fragment>
      ),
      renderTotal: (totalStats) => (
        <React.Fragment key="freeThrowsTotal">
          <TableCell className={classes.tableCell}>{fmtRatio(totalStats.made1Pointers, totalStats.attempted1Pointers)}</TableCell>
          <TableCell className={classes.tableCell}>{fmtPerc(totalStats.made1Pointers, totalStats.attempted1Pointers)}</TableCell>
        </React.Fragment>
      ),
      renderTeamOnly: (teamOnlyStats) => (
        <React.Fragment key="freeThrowsTeamOnly">
          <TableCell className={classes.tableCell}></TableCell>
          <TableCell className={classes.tableCell}></TableCell>
        </React.Fragment>
      ),
    },
    {
      displayName: 'Offensive Rebounds',
      internalKeys: ['offensiveRebounds'],
      headerLabels: ['OR'],
      colSpan: 1,
      render: (player) => <TableCell key="offensiveRebounds" className={classes.tableCell}>{player.offensiveRebounds}</TableCell>,
      renderTotal: (totalStats) => <TableCell key="offensiveReboundsTotal" className={classes.tableCell}>{totalStats.offensiveRebounds}</TableCell>,
      renderTeamOnly: (teamOnlyStats) => <TableCell key="offensiveReboundsTeamOnly" className={classes.tableCell}>{teamOnlyStats.offensiveRebounds}</TableCell>,
    },
    {
      displayName: 'Defensive Rebounds',
      internalKeys: ['defensiveRebounds'],
      headerLabels: ['DR'],
      colSpan: 1,
      render: (player) => <TableCell key="defensiveRebounds" className={classes.tableCell}>{player.defensiveRebounds}</TableCell>,
      renderTotal: (totalStats) => <TableCell key="defensiveReboundsTotal" className={classes.tableCell}>{totalStats.defensiveRebounds}</TableCell>,
      renderTeamOnly: (teamOnlyStats) => <TableCell key="defensiveReboundsTeamOnly" className={classes.tableCell}>{teamOnlyStats.defensiveRebounds}</TableCell>,
    },
    {
      displayName: 'REBOUNDS',
      internalKeys: ['offensiveRebounds', 'defensiveRebounds', 'totalRebounds'],
      headerLabels: ['OR', 'DR', 'TR'],
      colSpan: 3,
      render: (player) => (
        <React.Fragment key="totalRebounds">
          <TableCell className={classes.tableCell}>{player.offensiveRebounds}</TableCell>
          <TableCell className={classes.tableCell}>{player.defensiveRebounds}</TableCell>
          <TableCell className={classes.tableCell}>{player.totalRebounds}</TableCell>
        </React.Fragment>
      ),
      renderTotal: (totalStats) => (
        <React.Fragment key="totalReboundsTotal">
          <TableCell className={classes.tableCell}>{totalStats.offensiveRebounds}</TableCell>
          <TableCell className={classes.tableCell}>{totalStats.defensiveRebounds}</TableCell>
          <TableCell className={classes.tableCell}>{totalStats.rebounds}</TableCell>
        </React.Fragment>
      ),
      renderTeamOnly: (teamOnlyStats) => (
        <React.Fragment key="totalReboundsTeamOnly">
          <TableCell className={classes.tableCell}>{teamOnlyStats.offensiveRebounds}</TableCell>
          <TableCell className={classes.tableCell}>{teamOnlyStats.defensiveRebounds}</TableCell>
          <TableCell className={classes.tableCell}>{teamOnlyStats.rebounds}</TableCell>
        </React.Fragment>
      ),
    },
    {
      displayName: 'Assists',
      internalKeys: ['assists'],
      headerLabels: ['AST'],
      colSpan: 1,
      render: (player) => <TableCell key="assists" className={classes.tableCell}>{player.assists}</TableCell>,
      renderTotal: (totalStats) => <TableCell key="assistsTotal" className={classes.tableCell}>{totalStats.assists}</TableCell>,
      renderTeamOnly: (teamOnlyStats) => <TableCell key="assistsTeamOnly" className={classes.tableCell}>{teamOnlyStats.assists}</TableCell>,
    },
    {
      displayName: 'Steals',
      internalKeys: ['steals'],
      headerLabels: ['STL'],
      colSpan: 1,
      render: (player) => <TableCell key="steals" className={classes.tableCell}>{player.steals}</TableCell>,
      renderTotal: (totalStats) => <TableCell key="stealsTotal" className={classes.tableCell}>{totalStats.steals}</TableCell>,
      renderTeamOnly: (teamOnlyStats) => <TableCell key="stealsTeamOnly" className={classes.tableCell}>{teamOnlyStats.steals}</TableCell>,
    },
    {
      displayName: 'Turnovers',
      internalKeys: ['turnOvers'],
      headerLabels: ['TO'],
      colSpan: 1,
      render: (player) => <TableCell key="turnOvers" className={classes.tableCell}>{player.turnOvers}</TableCell>,
      renderTotal: (totalStats) => <TableCell key="turnOversTotal" className={classes.tableCell}>{totalStats.turnovers}</TableCell>,
      renderTeamOnly: (teamOnlyStats) => <TableCell key="turnOversTeamOnly" className={classes.tableCell}>{teamOnlyStats.turnovers}</TableCell>,
    },
    {
      displayName: 'Blocks',
      internalKeys: ['blocks'],
      headerLabels: ['BLK'],
      colSpan: 1,
      render: (player) => <TableCell key="blocks" className={classes.tableCell}>{player.blocks}</TableCell>,
      renderTotal: (totalStats) => <TableCell key="blocksTotal" className={classes.tableCell}>{totalStats.blocks}</TableCell>,
      renderTeamOnly: (teamOnlyStats) => <TableCell key="blocksTeamOnly" className={classes.tableCell}>{teamOnlyStats.blocks}</TableCell>,
    },
    {
      displayName: 'Blocks Against',
      internalKeys: ['blocksAgainst'],
      headerLabels: ['BLKA'],
      colSpan: 1,
      render: (player) => <TableCell key="blocksAgainst" className={classes.tableCell}>{player.blocksAgainst}</TableCell>,
      renderTotal: (totalStats) => <TableCell key="blocksAgainstTotal" className={classes.tableCell}>{totalStats.blocksAgainst}</TableCell>,
      renderTeamOnly: (teamOnlyStats) => <TableCell key="blocksAgainstTeamOnly" className={classes.tableCell}>{teamOnlyStats.blocksAgainst}</TableCell>,
    },
    {
      displayName: 'FOULS',
      internalKeys: ['foulsCommited', 'foulsDrawn'],
      headerLabels: ['FC', 'FD'],
      colSpan: 2,
      render: (player) => (
        <React.Fragment key="fouls">
          <TableCell className={classes.tableCell}>{player.foulsCommited}</TableCell>
          <TableCell className={classes.tableCell}>{player.foulsDrawn}</TableCell>
        </React.Fragment>
      ),
      renderTotal: (totalStats) => (
        <React.Fragment key="foulsTotal">
          <TableCell className={classes.tableCell}>{totalStats.fouls}</TableCell>
          <TableCell className={classes.tableCell}>{totalStats.foulsDrawn}</TableCell>
        </React.Fragment>
      ),
      renderTeamOnly: (teamOnlyStats) => (
        <React.Fragment key="foulsTeamOnly">
          <TableCell className={classes.tableCell}>{teamOnlyStats.fouls}</TableCell>
          <TableCell className={classes.tableCell}>{teamOnlyStats.foulsDrawn}</TableCell>
        </React.Fragment>
      ),
    },
    {
      displayName: 'PIR',
      internalKeys: ['pir'],
      headerLabels: ['PIR'],
      colSpan: 1,
      render: (player) => <TableCell key="pir" className={classes.tableCell}>{player.pir}</TableCell>,
      renderTotal: (totalStats) => <TableCell key="pirTotal" className={classes.tableCell}>{totalStats.pir}</TableCell>,
      renderTeamOnly: (teamOnlyStats) => <TableCell key="pirTeamOnly" className={classes.tableCell}>{teamOnlyStats.pir}</TableCell>,
    },
  ];

  const filteredHeaderGroups = visibleGroups
    ? headerGroupsConfig.filter(group => visibleGroups.has(group.displayName))
    : headerGroupsConfig;

  const totalColSpan = filteredHeaderGroups.reduce((sum, group) => sum + group.colSpan, 0);


  return (
    <Table className={classes.table}>
      <TableHead>
        <TableRow className={classes.tableHeader}>
          <TableCell className={classes.playerCell}>
            <div className={classes.containerDiv}>
              <span className={classes.span}>&nbsp;</span>
              <span style={{ marginLeft: 5 }}>{translate('ra.text.players')}</span>
            </div>
          </TableCell>
          {filteredHeaderGroups.map((group, index) => (
            <TableCell key={group.displayName} colSpan={group.colSpan} className={classes.tableCellBorder}>
              <div className={classes.containerDiv}>
                <span className={classes.span}>{group.colSpan === 1 ? <>&nbsp;</> : group.displayName}</span>
                <div className={classes.div}>
                  {group.headerLabels.map((label, i) => <span key={i}>{label}</span>)}
                </div>
              </div>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {/* Render players who played */}
        {playedPlayers.map((player) => (
          <React.Fragment key={player.id || player.gameTeamPlayerId}>
            <TableRow
              onClick={() => handleRowClick(player.id, player)}
              className={player?.id ? classes.hoverRow : ''}
              style={{ cursor: player?.id || !readOnly ? 'pointer' : 'default' }}
            >
            <TableCell>
              <div className={classes.ellipsisDiv} style={{ display: 'flex', alignItems: 'center' }}>
                {!player.id && (
                  <Tooltip title="Game player is not matched to a team player" arrow>
                    <Warning style={{ color: '#ff9800', fontSize: 18, marginLeft: 5 }} />
                  </Tooltip>
                )}
                <span style={{ fontWeight: 'bold', marginLeft: 5 }}>{player.jerseyNumber}</span>
                <span style={{ marginLeft: 5 }}>{formatFullName(player.fullName)}</span>
              </div>
            </TableCell>
            {filteredHeaderGroups.map(group => group.render(player))}
          </TableRow>

            {/* Dropdown row for player matching */}
            {!player.id && expandedRow === player.gameTeamPlayerId && (
              <TableRow>
                <TableCell colSpan={totalColSpan + 1} style={{ paddingBottom: 0, paddingTop: 0, backgroundColor: '#fafafa' }}>
                  <PlayerMatchingDropdown
                    isOpen={expandedRow === player.gameTeamPlayerId}
                    gameTeamPlayerId={player.gameTeamPlayerId}
                    eventTeamId={eventTeamId}
                    seasonTeamId={seasonTeamId}
                    gameId={gameId}
                    excludePlayerIds={matchedPlayerIds}
                    onClose={() => setExpandedRow(null)}
                    playerFullName={player.fullName}
                    playerJerseyNumber={player.jerseyNumber}
                  />
                </TableCell>
              </TableRow>
            )}
          </React.Fragment>
        ))}
        {/* Render players who did not play */}
        {didNotPlayPlayers.map((player) => (
          <React.Fragment key={player.id || player.gameTeamPlayerId}>
            <TableRow
              onClick={() => handleRowClick(player.id, player)}
              className={player?.id ? classes.hoverRow : ''}
              style={{ cursor: player?.id || !readOnly ? 'pointer' : 'default' }}
            >
              <TableCell>
                <div className={classes.ellipsisDiv} style={{ display: 'flex', alignItems: 'center' }}>
                  {!player.id && (
                    <Tooltip title="Game player is not matched to a team player" arrow>
                      <Warning style={{ color: '#ff9800', fontSize: 18, marginLeft: 5 }} />
                    </Tooltip>
                  )}
                  <span style={{ fontWeight: 'bold', marginLeft: 5 }}>{player.jerseyNumber}</span>
                  <span style={{ marginLeft: 5 }}>{formatFullName(player.fullName)}</span>
                </div>
              </TableCell>
              <TableCell colSpan={totalColSpan} className={classes.tableCell}>
                {translate('ra.text.didNotPlay')}
              </TableCell>
            </TableRow>

            {/* Dropdown row for player matching */}
            {!player.id && expandedRow === player.gameTeamPlayerId && (
              <TableRow>
                <TableCell colSpan={totalColSpan + 1} style={{ paddingBottom: 0, paddingTop: 0, backgroundColor: '#fafafa' }}>
                  <PlayerMatchingDropdown
                    isOpen={expandedRow === player.gameTeamPlayerId}
                    gameTeamPlayerId={player.gameTeamPlayerId}
                    eventTeamId={eventTeamId}
                    seasonTeamId={seasonTeamId}
                    gameId={gameId}
                    excludePlayerIds={matchedPlayerIds}
                    onClose={() => setExpandedRow(null)}
                    playerFullName={player.fullName}
                    playerJerseyNumber={player.jerseyNumber}
                  />
                </TableCell>
              </TableRow>
            )}
          </React.Fragment>
        ))}
        {teamOnlyStats && (
          <TableRow key={`team-only-stats-${eventTeamId}`}>
            <TableCell>
              <div className={classes.ellipsisDiv}>
                <span style={{ marginLeft: 10 }}>Team/Bench</span>
              </div>
            </TableCell>

            {filteredHeaderGroups.map(group => group.renderTeamOnly(teamOnlyStats))}
          </TableRow>
        )}
        {totalStats && (
          <TableRow key='total-stats'>
            <TableCell>
              <div className={classes.ellipsisDiv}>
                <span style={{ marginLeft: 10 }}>
                  <b>Total</b>
                </span>
              </div>
            </TableCell>

            {filteredHeaderGroups.map(group => group.renderTotal(totalStats))}
          </TableRow>
        )}
      </TableBody>
    </Table>
  )
}

// Define PropTypes for the component
PlayersPerGameTable.propTypes = {
  players: PropTypes.arrayOf(
    PropTypes.shape({
      playerId: PropTypes.string,
      gameTeamPlayerId: PropTypes.string,
      fullName: PropTypes.string,
      jerseyNumber: PropTypes.number,
      opponentName: PropTypes.string,
      gamesPlayed: PropTypes.number,
      gamesStarted: PropTypes.number,
      minutes: PropTypes.string,
      gameStats: PropTypes.shape({
        points: PropTypes.number,
        avgPoints: PropTypes.number,
        twoPointer: PropTypes.number,
        avgTwoPoints: PropTypes.number,
        totalTwoPointsAttempts: PropTypes.number,
        threePointer: PropTypes.number,
        avgThreePoints: PropTypes.number,
        totalThreePointsAttempts: PropTypes.number,
        freeThrows: PropTypes.number,
        avgFreeThrows: PropTypes.number,
        totalFreeThrowsAttempts: PropTypes.number,
        offensiveRebounds: PropTypes.number,
        avgOffensiveRebounds: PropTypes.number,
        defensiveRebounds: PropTypes.number,
        avgDefensiveRebounds: PropTypes.number,
        totalRebounds: PropTypes.number,
        avgRebounds: PropTypes.number,
        assists: PropTypes.number,
        avgAssists: PropTypes.number,
        steals: PropTypes.number,
        avgSteals: PropTypes.number,
        turnOvers: PropTypes.number,
        avgTurnOvers: PropTypes.number,
        blocksAgainst: PropTypes.number,
        avgBlocksAgainst: PropTypes.number,
        foulsCommited: PropTypes.number,
        avgFoulsCommited: PropTypes.number,
        foulsDrawn: PropTypes.number,
        avgFoulsDrawn: PropTypes.number,
        pir: PropTypes.number,
        avgPIR: PropTypes.number,
      }),
    }),
  ),
  teamOnlyStats: PropTypes.object,
  totalStats: PropTypes.object,
  eventTeamId: PropTypes.string,
  seasonTeamId: PropTypes.string,
  gameId: PropTypes.string,
  displayStats: PropTypes.array,
}

export default PlayersPerGameTable
